import { useNuxtApp } from '#app'

/**
 * Composable simplificado para Intro.js
 * Baseado na documentação oficial: https://introjs.com/docs
 */

export interface TourStep {
  element?: string
  intro: string
  title?: string
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto'
}

export interface TourCallbacks {
  onStart?: () => void
  onExit?: () => void
  onComplete?: () => void
  onBeforeChange?: (targetElement: HTMLElement) => void
  onAfterChange?: (targetElement: HTMLElement) => void
  onChange?: (targetElement: HTMLElement) => void
}

export interface TourOptions {
  steps?: TourStep[]
  nextLabel?: string
  prevLabel?: string
  doneLabel?: string
  skipLabel?: string
  showProgress?: boolean
  showBullets?: boolean
  exitOnEsc?: boolean
  exitOnOverlayClick?: boolean
  scrollToElement?: boolean
  scrollPadding?: number
  callbacks?: TourCallbacks
}

/**
 * Composable para Intro.js (API Simplificada)
 * Seguindo documentação oficial: https://introjs.com/docs
 */
export function useIntroJs (): any {
  const nuxtApp = useNuxtApp()
  const introJs = (nuxtApp as any).$introJs

  /**
   * Configura callbacks para um tour
   */
  const setupCallbacks = (intro: any, callbacks: TourCallbacks): void => {
    if (!intro || !callbacks) { return }

    if (callbacks.onStart) {
      intro.onstart(callbacks.onStart)
    }

    if (callbacks.onExit) {
      intro.onexit(callbacks.onExit)
    }

    if (callbacks.onComplete) {
      intro.oncomplete(callbacks.onComplete)
    }

    if (callbacks.onBeforeChange) {
      intro.onbeforechange(callbacks.onBeforeChange)
    }

    if (callbacks.onAfterChange) {
      intro.onafterchange(callbacks.onAfterChange)
    }

    if (callbacks.onChange) {
      intro.onchange(callbacks.onChange)
    }
  }

  /**
   * Inicia tour baseado em data-intro attributes (método oficial)
   * Ref: https://introjs.com/docs
   */
  const startDataTour = (selector?: string, options?: TourOptions): any => {
    if (!introJs) {
      return null
    }

    const intro = selector ? introJs(selector) : introJs()

    if (options) {
      const { callbacks, ...introOptions } = options
      intro.setOptions(introOptions)

      // Configurar callbacks se fornecidos
      if (callbacks) {
        setupCallbacks(intro, callbacks)
      }
    }

    intro.start()
    return intro
  }

  /**
   * Inicia tour com steps programáticos
   */
  const startTour = (steps: TourStep[], options?: TourOptions): any => {
    if (!introJs) {
      return null
    }

    // Filtrar steps válidos
    const validSteps = steps.filter((step) => {
      if (!step.element) {
        return true
      }
      return document.querySelector(step.element) !== null
    })

    if (validSteps.length === 0) {
      return null
    }

    const { callbacks, ...introOptions } = options || {}

    const intro = introJs().setOptions({
      steps: validSteps,
      showProgress: true,
      exitOnEsc: true,
      exitOnOverlayClick: true,
      ...introOptions
    })

    // Configurar callbacks se fornecidos
    if (callbacks) {
      setupCallbacks(intro, callbacks)
    }

    intro.start()
    return intro
  }

  /**
   * Adiciona dica a um elemento
   */
  const addHint = (element: string, text: string): void => {
    const el = document.querySelector(element)
    if (el) {
      el.setAttribute('data-hint', text)
      el.setAttribute('data-hintposition', 'top-middle')
    }
  }

  /**
   * Mostra todas as dicas
   */
  const showHints = (): void => {
    if (introJs) {
      introJs().addHints()
      introJs().showHints()
    }
  }

  /**
   * Para tour atual
   */
  const exitTour = (): void => {
    if (introJs) {
      introJs().exit()
    }
  }

  /**
   * Inicia tour com callbacks simplificados
   */
  const startTourWithCallbacks = (
    selector?: string,
    steps?: TourStep[],
    callbacks?: TourCallbacks,
    options?: Omit<TourOptions, 'callbacks'>
  ): any => {
    let intro

    if (steps && steps.length > 0) {
      intro = startTour(steps, { ...options, callbacks })
    } else {
      intro = startDataTour(selector, { ...options, callbacks })
    }

    if (intro && callbacks) {
      setupCallbacks(intro, callbacks)
    }

    return intro
  }

  return {
    startDataTour, // Tour usando data-intro (método oficial)
    startTour, // Tour programático
    addHint, // Adicionar dicas
    showHints, // Mostrar dicas
    exitTour, // Sair do tour
    setupCallbacks, // Configurar callbacks
    startTourWithCallbacks // Tour com callbacks simplificado
  }
}
